import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { Select, Slider } from 'antd';
import axios from 'axios';
import debounce from 'lodash/debounce';
import { API_BASE_URL } from '../config';
import './FilterSidebar.css';

const MIN_PRICE = 0;
const MAX_PRICE = 2000;

// ── Primary color swatches ────────────────────────────────────────────────────
const PRIMARY_COLORS = [
  { name: 'Black',     hex: '#1a1a1a' },
  { name: 'Grey',      hex: '#9ca3af' },
  { name: 'White',     hex: '#f1f5f9', border: true },
  { name: 'Brown',     hex: '#7c4a1e' },
  { name: 'Caramel',   hex: '#c08040' },
  { name: 'Amber',     hex: '#f59e0b' },
  { name: 'Honey',     hex: '#e8a020' },
  { name: 'Gold',      hex: '#d4a017' },
  { name: 'Hazel',     hex: '#8b6914' },
  { name: 'Green',     hex: '#22c55e' },
  { name: 'Aqua',      hex: '#06b6d4' },
  { name: 'Turquoise', hex: '#14b8a6' },
  { name: 'Blue',      hex: '#3b82f6' },
  { name: 'Violet',    hex: '#8b5cf6' },
  { name: 'Pink',      hex: '#ec4899' },
  { name: 'Red',       hex: '#ef4444' },
  { name: 'Orange',    hex: '#f97316' },
  { name: 'Yellow',    hex: '#eab308' },
];

/* ── Skeleton block ── */
const FilterSkeleton = () => (
  <div className="bg-white rounded-2xl border border-sand shadow-card p-5 space-y-6">
    <div className="h-6 w-20 rounded skeleton-shimmer" />
    {[100, 80, 90, 70].map((w, i) => (
      <div key={i} className="space-y-2">
        <div className={`h-4 w-${w === 100 ? 'full' : `${w}`} rounded skeleton-shimmer`} style={{ width: `${w}%` }} />
        <div className="h-9 rounded-xl skeleton-shimmer" />
      </div>
    ))}
  </div>
);

/* ── Section header ── */
const SectionLabel = ({ children }) => (
  <p className="text-xs font-semibold uppercase tracking-widest text-clay mb-2.5">
    {children}
  </p>
);

const FilterSidebar = ({ onFilterChange, categoryId, initialBrand, voiceFilters }) => {
  const [priceRange, setPriceRange]           = useState([MIN_PRICE, MAX_PRICE]);
  const [powerRange, setPowerRange]           = useState([-8, 12]);
  const [selectedBrand, setSelectedBrand]     = useState(initialBrand?.id || null);
  const [selectedDuration, setSelectedDuration] = useState(null);
  const [selectedColor, setSelectedColor]           = useState(null);
  const [selectedPrimaryColor, setSelectedPrimaryColor] = useState(null);
  const [selectedMaterial, setSelectedMaterial]     = useState(null);

  // Apply voice-search filters to the sidebar UI when they arrive
  useEffect(() => {
    if (!voiceFilters) return;
    setSelectedBrand(voiceFilters.subCategory?.toString() || null);
    setPriceRange([
      voiceFilters.price?.gte ?? MIN_PRICE,
      voiceFilters.price?.lte ?? MAX_PRICE,
    ]);
    setSelectedColor(null);
    setSelectedPrimaryColor(voiceFilters.primaryColor || null);
    setSelectedDuration(voiceFilters.duration || null);
    setSelectedMaterial(voiceFilters.lensesMaterial || null);
  }, [voiceFilters]); // eslint-disable-line
  const [brands, setBrands]                   = useState([]);
  const [colorOptions, setColorOptions]       = useState([]);
  const [materialOptions, setMaterialOptions] = useState([]);
  const [durationOptions, setDurationOptions] = useState([]);
  const [loading, setLoading]                 = useState(true);
  const [isSliding, setIsSliding]             = useState(false);

  /* Fetch filter options */
  useEffect(() => {
    const fetchFilters = async () => {
      setLoading(true);
      try {
        const [brandsRes, optionsRes] = await Promise.all([
          categoryId
            ? axios.get(`${API_BASE_URL}/categories/${categoryId}/subcategories`)
            : Promise.resolve({ data: { data: [] } }),
          axios.get(`${API_BASE_URL}/products/filter-options`, {
            params: categoryId ? { category: categoryId } : {},
          }),
        ]);

        setBrands(brandsRes.data.data.map((b) => ({ value: b._id, label: b.name })));

        const opts = optionsRes.data.data || {};
        setColorOptions((opts.colors || []).map((c) => ({ value: c, label: c })));
        setMaterialOptions((opts.materials || []).map((m) => ({ value: m, label: m })));
        setDurationOptions(
          (opts.durations || []).length > 0
            ? opts.durations.map((d) => ({ value: d, label: d }))
            : [
                { value: 'Daily',   label: 'Daily'   },
                { value: 'Weekly',  label: 'Weekly'  },
                { value: 'Monthly', label: 'Monthly' },
                { value: 'Yearly',  label: 'Yearly'  },
              ]
        );
      } catch {
        setDurationOptions([
          { value: 'Daily',   label: 'Daily'   },
          { value: 'Weekly',  label: 'Weekly'  },
          { value: 'Monthly', label: 'Monthly' },
          { value: 'Yearly',  label: 'Yearly'  },
        ]);
      } finally {
        setLoading(false);
      }
    };
    fetchFilters();
  }, [categoryId]);

  const generateFilters = useCallback(() => {
    const f = {};
    if (priceRange[0] > MIN_PRICE || priceRange[1] < MAX_PRICE)
      f.price = { gte: priceRange[0], lte: priceRange[1] };
    if (powerRange[0] > -8 || powerRange[1] < 12) {
      f.minEyePower = powerRange[0];
      f.maxEyePower = powerRange[1];
    }
    if (selectedBrand)        f.subCategory     = selectedBrand;
    if (selectedDuration)     f.duration        = selectedDuration;
    if (selectedColor)        f.colorsAvailable = selectedColor;
    if (selectedPrimaryColor) f.primaryColor    = selectedPrimaryColor;
    if (selectedMaterial)     f.lensesMaterial  = selectedMaterial;
    return f;
  }, [priceRange, powerRange, selectedBrand, selectedDuration, selectedColor, selectedPrimaryColor, selectedMaterial]);

  const debouncedFilter = useCallback(
    debounce((filters) => {
      setTimeout(() => { onFilterChange(filters); setIsSliding(false); }, 0);
    }, 300),
    [onFilterChange]
  );

  useEffect(() => () => debouncedFilter.cancel(), [debouncedFilter]);

  /* Slider handlers */
  const handlePriceChange      = (v) => { setIsSliding(true); setPriceRange(v); };
  const handlePriceAfterChange = (v) => { setPriceRange(v); setIsSliding(false); debouncedFilter(generateFilters()); };
  const handlePowerChange      = (v) => { setIsSliding(true); setPowerRange(v); };
  const handlePowerAfterChange = (v) => { setPowerRange(v); setIsSliding(false); debouncedFilter(generateFilters()); };

  /* Select handlers */
  const makeSelectHandler = (setter, filterKey) => (value) => {
    setter(value);
    const updated = { ...generateFilters() };
    if (value) updated[filterKey] = value;
    else delete updated[filterKey];
    debouncedFilter(updated);
  };

  const handleBrandChange    = makeSelectHandler(setSelectedBrand,    'subCategory');
  const handleDurationChange = makeSelectHandler(setSelectedDuration, 'duration');
  const handleColorChange = (value) => {
    setSelectedColor(value);
    setSelectedPrimaryColor(null); // mutually exclusive
    const updated = { ...generateFilters() };
    delete updated.primaryColor;
    if (value) updated.colorsAvailable = value;
    else delete updated.colorsAvailable;
    debouncedFilter(updated);
  };

  const handlePrimaryColorChange = (name) => {
    const next = selectedPrimaryColor === name ? null : name;
    setSelectedPrimaryColor(next);
    setSelectedColor(null); // mutually exclusive
    const updated = { ...generateFilters() };
    delete updated.colorsAvailable;
    if (next) updated.primaryColor = next;
    else delete updated.primaryColor;
    debouncedFilter(updated);
  };
  const handleMaterialChange = (value) => {
    setSelectedMaterial(value);
    const updated = { ...generateFilters() };
    if (value) updated.lensesMaterial = value;
    else delete updated.lensesMaterial;
    debouncedFilter(updated);
  };

  const hasActiveFilters = useMemo(
    () =>
      priceRange[0] !== MIN_PRICE ||
      priceRange[1] !== MAX_PRICE ||
      powerRange[0] !== -8 ||
      powerRange[1] !== 12 ||
      selectedBrand        !== null ||
      selectedDuration     !== null ||
      selectedColor        !== null ||
      selectedPrimaryColor !== null ||
      selectedMaterial     !== null,
    [priceRange, powerRange, selectedBrand, selectedDuration, selectedColor, selectedPrimaryColor, selectedMaterial]
  );

  const activeCount = useMemo(
    () =>
      [
        priceRange[0] !== MIN_PRICE || priceRange[1] !== MAX_PRICE,
        powerRange[0] !== -8 || powerRange[1] !== 12,
        selectedBrand, selectedDuration, selectedColor || selectedPrimaryColor, selectedMaterial,
      ].filter(Boolean).length,
    [priceRange, powerRange, selectedBrand, selectedDuration, selectedColor, selectedPrimaryColor, selectedMaterial]
  );

  const handleReset = () => {
    setPriceRange([MIN_PRICE, MAX_PRICE]);
    setPowerRange([-8, 12]);
    setSelectedBrand(null);
    setSelectedDuration(null);
    setSelectedColor(null);
    setSelectedPrimaryColor(null);
    setSelectedMaterial(null);
    setIsSliding(false);
    onFilterChange({});
  };

  if (loading) return <FilterSkeleton />;

  return (
    <div className="bg-white rounded-2xl border border-sand shadow-card hover:shadow-card-hover p-5 space-y-5 md:sticky md:top-24 md:max-h-[calc(100vh-7rem)] md:overflow-y-auto">

      {/* Header */}
      <div className="flex items-center justify-between pb-1">
        <div className="flex items-center gap-2">
          <h3 className="font-display font-normal tracking-tight text-base text-ink">Filters</h3>
          {activeCount > 0 && (
            <span className="bg-brand-500 text-ink text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center leading-none">
              {activeCount}
            </span>
          )}
        </div>
        {hasActiveFilters && (
          <button
            onClick={handleReset}
            className="text-xs text-red-500 hover:text-red-600 font-semibold transition-colors duration-150"
          >
            Reset all
          </button>
        )}
      </div>

      <div className="border-t border-sand" />

      {/* Price Range */}
      <div>
        <SectionLabel>Price (EGP)</SectionLabel>
        <div className="px-1 pt-1 pb-2">
          <Slider
            range
            min={MIN_PRICE}
            max={MAX_PRICE}
            value={priceRange}
            onChange={handlePriceChange}
            onAfterChange={handlePriceAfterChange}
          />
        </div>
        <div className="flex justify-between text-sm font-medium text-clay">
          <span className="bg-cream border border-sand rounded-lg px-2 py-0.5 text-xs">
            EGP {priceRange[0].toLocaleString()}
          </span>
          <span className="bg-cream border border-sand rounded-lg px-2 py-0.5 text-xs">
            EGP {priceRange[1].toLocaleString()}
          </span>
        </div>
        {isSliding && (
          <p className="text-brand-600 text-xs mt-1.5 animate-pulse font-medium">Updating…</p>
        )}
      </div>

      {/* Eye Power Finder CTA — only for lens categories */}
      {durationOptions.length > 0 && <EyePowerFinderCTA />}

      {/* Power Range — only for lens categories */}
      {durationOptions.length > 0 && <div>
        <SectionLabel>Power Range</SectionLabel>
        <div className="px-1 pt-1 pb-2">
          <Slider
            range
            min={-8}
            max={12}
            step={0.25}
            value={powerRange}
            onChange={handlePowerChange}
            onAfterChange={handlePowerAfterChange}
            tooltip={{ formatter: (v) => `${v}` }}
          />
        </div>
        <div className="flex justify-between">
          <span className="bg-cream border border-sand rounded-lg px-2 py-0.5 text-xs font-medium text-clay">
            {powerRange[0]}
          </span>
          <span className="bg-cream border border-sand rounded-lg px-2 py-0.5 text-xs font-medium text-clay">
            {powerRange[1]}
          </span>
        </div>
      </div>}

      {/* Brand — only when multiple brands exist */}
      {brands.length > 1 && (
        <div>
          <SectionLabel>Brand</SectionLabel>
          <Select
            placeholder="All brands"
            value={selectedBrand}
            onChange={handleBrandChange}
            className="w-full"
            allowClear
            options={brands}
            size="large"
          />
        </div>
      )}

      {/* Duration — only for lens categories */}
      {durationOptions.length > 0 && (
      <div>
        <SectionLabel>Duration</SectionLabel>
        <Select
          placeholder="All durations"
          value={selectedDuration}
          onChange={handleDurationChange}
          className="w-full"
          allowClear
          options={durationOptions}
          size="large"
        />
      </div>
      )}

      {/* Color — only for lens categories */}
      {durationOptions.length > 0 && colorOptions.length > 0 && (
        <div>
          <SectionLabel>Color</SectionLabel>

          {/* ── Primary color swatches ── */}
          <p className="text-[10px] font-semibold text-clay uppercase tracking-wider mb-2">
            By family
          </p>
          <div className="flex flex-wrap gap-x-2 gap-y-3 mb-3">
            {PRIMARY_COLORS.map(({ name, hex, border }) => {
              const active = selectedPrimaryColor === name;
              return (
                <button
                  key={name}
                  title={name}
                  onClick={() => handlePrimaryColorChange(name)}
                  className="flex flex-col items-center gap-1 active:scale-95 transition-transform"
                  style={{ width: 40, minHeight: 44 }} /* 44px min touch target */
                >
                  <span
                    className="block rounded-full transition-all duration-150"
                    style={{
                      width: 28,
                      height: 28,
                      background: hex,
                      border: border ? '1.5px solid #cbd5e1' : '1.5px solid transparent',
                      boxShadow: active
                        ? `0 0 0 2.5px #fff, 0 0 0 4.5px ${hex}`
                        : 'none',
                      transform: active ? 'scale(1.12)' : 'scale(1)',
                    }}
                  />
                  <span
                    className="text-[9px] font-medium leading-none text-center"
                    style={{ color: active ? '#b9772a' : '#94a3b8' }}
                  >
                    {name}
                  </span>
                </button>
              );
            })}
          </div>

          {/* ── Exact shade dropdown ── */}
          <p className="text-[10px] font-semibold text-clay uppercase tracking-wider mb-1.5">
            Exact shade
          </p>
          <Select
            placeholder="All shades"
            value={selectedColor}
            onChange={handleColorChange}
            className="w-full"
            allowClear
            options={colorOptions}
            size="large"
            showSearch
            filterOption={(input, opt) =>
              opt.label.toLowerCase().includes(input.toLowerCase())
            }
          />
        </div>
      )}

      {/* Material */}
      {materialOptions.length > 0 && (
        <div>
          <SectionLabel>Material</SectionLabel>
          <Select
            placeholder="All materials"
            value={selectedMaterial}
            onChange={handleMaterialChange}
            className="w-full"
            allowClear
            options={materialOptions}
            size="large"
          />
        </div>
      )}
    </div>
  );
};

// ── Eye Power Finder CTA card ─────────────────────────────────────────────────
function EyePowerFinderCTA() {
  const prescription = useSelector((state) => state.prescription);

  if (prescription.isSet) {
    return (
      <div className="rounded-xl bg-ink text-cream px-3 py-2.5 text-xs">
        <div className="flex items-center gap-1.5 font-semibold mb-1">
          <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <circle cx="12" cy="12" r="3" /><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z" />
          </svg>
          Saved prescription
        </div>
        {(() => {
          const fmt = (eye) => {
            const sph = eye?.sph ?? null;
            if (sph === null || sph === undefined) return '—';
            if (sph === 0) return 'Plano';
            return `${sph > 0 ? '+' : ''}${Math.abs(sph).toFixed(2)}`;
          };
          return (
            <p className="text-cream/70 mb-2">
              OD {fmt(prescription.rightEye)} &nbsp;·&nbsp; OS {fmt(prescription.leftEye)}
            </p>
          );
        })()}
        <Link to="/eye-power-finder" className="underline underline-offset-2 text-brand-300 hover:text-cream">
          Edit prescription →
        </Link>
      </div>
    );
  }

  return (
    <Link
      to="/eye-power-finder"
      className="block rounded-xl border-2 border-dashed border-sand hover:border-brand-500 px-3 py-3 text-center transition-colors group"
    >
      <div className="flex items-center justify-center gap-1.5 text-sm font-semibold text-ink group-hover:text-brand-600">
        <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="3" /><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z" />
        </svg>
        Eye Power Finder
      </div>
      <p className="text-xs text-clay mt-0.5">Save your prescription for guidance</p>
    </Link>
  );
}

export default FilterSidebar;
